import {
	Button,
	LoaderScreen,
	Typography,
	RadioButton,
	Icon,
} from '@/components/common';
import { CaseQuestionI } from '@/interfaces';
import { InputCheck, InputDate, Textarea } from '@/components/form';
import { getYear } from 'date-fns';
import clsx from 'clsx';
import { useSurvey } from '@/hooks/useSurvey';
import { AvatarGroup } from '@/components/common/avatar/avatarGroup';
import Icons from '@/const/icons';
import { StatusCaseItem } from '@/interfaces';

interface Props {
	id: string;
	uuid: string;
	questions: CaseQuestionI[];
	previewMode?: boolean;
	status: StatusCaseItem;
}

export const Survey: React.FC<Props> = ({
	id,
	uuid,
	questions,
	previewMode,
	status,
}) => {
	const {
		loading,
		onChange,
		listQuestions,
		onClickSubmit,
		register,
		setValue,
		isButtonDisabled,
		onMultipleChange,
		watch,
		avatarItems,
	} = useSurvey({ id, uuid, questions });
	const isCaseClosed = status === 'Closed to new clients';

	if (listQuestions.length === 0)
		return <LoaderScreen className="min-h-screen" />;
	return (
		<>
			<div
				className="rounded-none xl:rounded-[32px] border border-black/8 bg-white mb-11 xl:mb-0 relative"
				style={{
					// background:
					// 	'linear-gradient(141.32deg, rgba(128, 57, 223, 0.2) 2.89%, #8039DF 97.83%)',
					boxShadow: '0px 14px 42px 0px #080F340F',
				}}
			>
				<div className="relative pt-6 xl:pt-12 pb-2 px-4 lg:px-12 ">
					<Typography type="bold-22" className="!text-[22px] leading-[150%]">
						See if You&apos;re{' '}
						<span className="text-primary">Owed Compensation</span>
					</Typography>
					<div className="flex items-center gap-2 mt-4 mb-7">
						<Icon
							src={Icons.shieldSecurity}
							width={18}
							height={18}
							className="shrink-0 text-green-120"
						/>
						<Typography type="regular-14" className="text-left text-gray-35">
							Secure, confidential, and 100% free to check. Your data is
							protected.
						</Typography>
					</div>
					{listQuestions?.map(
						(
							{
								id,
								question,
								question_type,
								answer,
								multiple_choise_questions,
							},
							index
						) => (
							<div className="space-y-2 mb-6 last:mb-0 relative" key={id}>
								<Typography
									type="semibold-18"
									className="font-normal !text-base lg:!text-lg flex items-center gap-2 xl:flex-none opacity-[0.50]"
								>
									{`${index + 1}.`}{' '}
									<span className="whitespace-pre-line">{question}</span>
								</Typography>
								{question_type === 'YES_NO' ? (
									<RadioButton
										value={answer}
										options={[
											{ id: question, value: 'YES', label: 'Yes' },
											{ id: question, value: 'NO', label: 'No' },
										]}
										defaultValue={undefined}
										orientation={'vertical'}
										onChange={(value) => onChange(value, id)}
										className="min-h-16"
										classNameLabel="!text-gray-35"
										classNameContainer="border border-grey-100 rounded-lg p-2"
										disabled={isCaseClosed}
									/>
								) : question_type === 'TEXT' ? (
									<Textarea
										name={`answers_${id}`}
										onChange={(value) => onChange(value.target.value, id)}
										register={register}
										placeholder="Write your answer here"
										classNameInput="!mt-0"
										className="z-10"
									/>
								) : question_type === 'MULTIPLE' ? (
									multiple_choise_questions?.map((option) => (
										<div key={option.id} className="flex items-center gap-4">
											<InputCheck
												name={`answer_${option.id}`}
												register={register}
												className="bg-white w-6"
												onChange={() => onMultipleChange(id, option.id)}
											/>
											<div className="flex items-center">
												{option.brand && (
													<Typography
														type="regular-16"
														className="!text-gray-35"
													>
														{option.brand}
													</Typography>
												)}
												{option.complement && (
													<Typography
														type="regular-16"
														className="!text-gray-35"
													>
														{`${option.brand ? ':' : ''} ${option.complement}`}
													</Typography>
												)}
											</div>
										</div>
									))
								) : (
									<InputDate
										dateRange="before-current"
										startYear={getYear(new Date()) - 100}
										endYear={getYear(new Date())}
										name={`answers_${id}`}
										register={register}
										setValueInput={setValue}
										className="w-full md:w-[404px]"
										classNameTrigger="w-full"
										classNameIcon="!top-[10px]"
										classNameInput="!placeholder-[#0D061633] !text-black !mt-0"
										placeholder="MM/DD/YYYY"
										onSelected={(value) => onChange(value, id)}
										dateDefault={watch(`answers_${id}`)}
									/>
								)}
								<div
									className={clsx(
										'absolute inset-0 bg-[#FFFFFF]/50 z-[1000] rounded-none xl:rounded-[32px] pointer-events-none',
										{
											hidden: !isCaseClosed,
										}
									)}
								/>
							</div>
						)
					)}
				</div>
				<div className="relative xl:border-t xl:border-grey-100 rounded-b-[32px] pt-4 px-4 lg:px-8 pb-6">
					<Button
						loading={loading}
						decoration="primary"
						size="fit"
						className={clsx('!w-full relative z-[999] md:!h-16', {
							hidden: previewMode,
							'pointer-events-none !cursor-not-allowed':
								isButtonDisabled || loading || !!previewMode,
						})}
						onClick={onClickSubmit}
						disabled={isCaseClosed}
					>
						<Typography
							type="regular-16"
							className="text-white"
							text="Get Started For Free"
						/>
					</Button>
					<div className="flex justify-center items-center w-full mt-4 lg:mt-4 gap-2 lg:gap-3">
						<AvatarGroup
							options={avatarItems}
							size="lg"
							className="hidden lg:flex"
						/>

						<Typography type="regular-12" className="!text-sm lg:!text-xs">
							Join <span className="font-semibold">+3,000</span> other Claim
							Magic claimants who are getting what they deserve
						</Typography>
					</div>
				</div>
			</div>
			<div className="mx-auto mt-6 hidden xl:block">
				<Typography
					type="regular-10"
					className="opacity-60 font-light xl:text-center !text-[10px] leading-4"
				>
					The information on this website is for general informational purposes
					only and is not legal advice. No attorney-client relationship is
					created unless and until a formal engagement agreement is signed. All
					your information is confidential and we will not sell or share them
					without your permission.
				</Typography>
			</div>
		</>
	);
};
